import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const ALLOWED_MODULES = [1, 3, 7, 16];
const ALLOWED_DEPTS   = [26, 75, 99, 102, 109, 110, 192, 210, 211, 214];

function playSound(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.pause();
  el.currentTime = 0;
  el.play().catch(() => {});
}

export default function MarkAttendance() {
  const navigate = useNavigate();

  const localUser = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  })();

  const moduleId   = parseInt(localUser?.moduleId, 10);
  const deptId     = parseInt(localUser?.deptid,   10);
  const employeeId = parseInt(localUser?.id,        10);

  const videoRef      = useRef(null);
  const canvasRef     = useRef(null);
  const streamRef     = useRef(null);
  const activeRef     = useRef(false);
  const processingRef = useRef(false);
  const timerRef      = useRef(null);

  const [status,   setStatus]   = useState("Starting camera…");
  const [camError, setCamError] = useState("");
  const [success,  setSuccess]  = useState(false);

  const stopCamera = useCallback(() => {
    activeRef.current = false;
    if (timerRef.current)  { clearTimeout(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    ["successSound", "failSound", "gpsSound"].forEach(id => {
      const el = document.getElementById(id);
      if (el) { el.pause(); el.currentTime = 0; }
    });
  }, []);

  const detectFace = useCallback(() => {
    if (!activeRef.current || processingRef.current) return;
    processingRef.current = true;

    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || video.readyState !== 4) {
      processingRef.current = false;
      return;
    }

    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL("image/jpeg");

    const scheduleRetry = () => {
      if (!activeRef.current) return;
      timerRef.current = setTimeout(() => {
        processingRef.current = false;
        detectFace();
      }, 4000);
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        if (!activeRef.current) { processingRef.current = false; return; }

        try {
          const res = await fetch("/api/attendance/check-face", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              employeeId,
              moduleId,
              image:     imageData,
              latitude:  pos.coords.latitude,
              longitude: pos.coords.longitude,
            }),
          });

          if (!activeRef.current) { processingRef.current = false; return; }
          const data = await res.json();
          if (!activeRef.current) { processingRef.current = false; return; }

          if (data.status === "success") {
            playSound("successSound");
            setSuccess(true);
            setStatus(`✅ ${data.name} — ${data.message} (confidence: ${Math.round(data.confidence)}%)`);
            stopCamera();
            setTimeout(() => navigate("/profile"), 3000);
            return;
          }

          if (data.status === "fail") {
            playSound("failSound");
            setStatus("Face not matched — retrying in 4 s…");
          } else if (data.status === "error") {
            if ((data.message || "").toLowerCase().includes("gps")) {
              playSound("gpsSound");
              setStatus(`⚠ ${data.message}`);
            } else {
              setStatus(`⚠ ${data.message}`);
            }
          }
        } catch {
          setStatus("Network error — retrying in 4 s…");
        }

        scheduleRetry();
      },
      () => {
        setStatus("GPS unavailable — enable location and retry.");
        processingRef.current = false;
        scheduleRetry();
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  }, [employeeId, moduleId, navigate, stopCamera]);

  useEffect(() => {
    let mounted = true;
    activeRef.current = true;

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: 640, height: 480 },
        });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        const video = videoRef.current;
        video.srcObject = stream;
        video.onloadedmetadata = () => video.play();
        video.onplay = () => {
          setStatus("Camera ready — looking for your face…");
          detectFace();
        };
      } catch {
        if (mounted) setCamError("Camera access denied. Allow camera permission and reload.");
      }
    })();

    return () => {
      mounted = false;
      stopCamera();
    };
  }, [detectFace, stopCamera]);

  if (!ALLOWED_MODULES.includes(moduleId) || !ALLOWED_DEPTS.includes(deptId)) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access.</div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h4 className="mb-3">
        <i className="bi bi-fingerprint me-2"></i>Face Recognition Attendance
      </h4>

      {camError ? (
        <div className="alert alert-danger">{camError}</div>
      ) : (
        <div className={`alert ${success ? "alert-success" : "alert-info"} py-2`}>
          {status}
        </div>
      )}

      <div style={{ display: "inline-block", borderRadius: 8, overflow: "hidden" }}>
        <video
          ref={videoRef}
          width={640}
          height={480}
          autoPlay
          playsInline
          muted
          style={{ display: "block", maxWidth: "100%" }}
        />
      </div>
      <canvas ref={canvasRef} width={640} height={480} style={{ display: "none" }} />

      <div className="mt-3">
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => { stopCamera(); navigate("/profile"); }}
        >
          <i className="bi bi-x-circle me-1"></i>Cancel
        </button>
      </div>

      {/* Audio feedback */}
      <audio id="successSound" src="/sounds/success.mp3"            preload="auto" />
      <audio id="failSound"    src="/sounds/failure.mp3"            preload="auto" />
      <audio id="gpsSound"     src="/sounds/gps_out_of_range.mp3"  preload="auto" />
    </div>
  );
}
